import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { apiFetch } from '../lib/api';

export type Role = 'owner' | 'admin' | 'analyst' | 'viewer';
export type Tier = 'trial' | 'starter' | 'pro' | 'enterprise';

export interface User {
  id: string;
  email: string;
  name: string | null;
  picture_url: string | null;
}

export interface AppUser extends User {
  role: Role;
  tier: Tier;
  onboarding_completed: boolean;
}

interface AuthContextValue {
  user: AppUser | null;
  isPending: boolean;
  isFetching: boolean;
  fetchUser: () => Promise<void>;
  redirectToLogin: () => Promise<void>;
  exchangeCodeForSessionToken: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (roles: Role | Role[]) => boolean;
  hasTier: (tiers: Tier | Tier[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const fetchUser = useCallback(async () => {
    setIsFetching(true);
    try {
      const userData = await apiFetch<AppUser>('/api/auth/users/me');
      setUser(userData);
    } catch {
      setUser(null);
    } finally {
      setIsFetching(false);
      setIsPending(false);
    }
  }, []);

  const redirectToLogin = useCallback(async () => {
    const data = await apiFetch<{ redirectUrl: string }>('/api/auth/oauth/google/redirect_url');
    window.location.href = data.redirectUrl;
  }, []);

  const exchangeCodeForSessionToken = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code || !state) {
      throw new Error('OAuth callback is missing code or state');
    }

    const response = await fetch('/api/auth/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code');
    }

    await fetchUser();
  }, [fetchUser]);

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', {
      credentials: 'include',
    });
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (roles: Role | Role[]) => {
      if (!user) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(user.role);
    },
    [user]
  );

  const hasTier = useCallback(
    (tiers: Tier | Tier[]) => {
      if (!user) return false;
      const tierArray = Array.isArray(tiers) ? tiers : [tiers];
      return tierArray.includes(user.tier);
    },
    [user]
  );

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isPending,
        isFetching,
        fetchUser,
        redirectToLogin,
        exchangeCodeForSessionToken,
        logout,
        hasRole,
        hasTier,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
